import { useState } from 'react';
import { SalesDoc } from '../types';
import { format } from 'date-fns';
import { formatINR } from '@/shared/utils/currency';

export function PrintableDocument({ doc }: { doc: SalesDoc }) {
  // Capture a stable "generated" fallback timestamp once at mount so render stays
  // pure (used only when the document has no persisted created_at).
  const [generatedFallback] = useState(() => Date.now());
  if (!doc.template_data) return <div className="p-8 text-center text-rose-500">Document data missing.</div>;
  
  const td = doc.template_data;
  
  return (
    <div className="p-10 bg-white text-black min-h-[600px] font-sans" id={`print-doc-${doc.id}`}>
      <div className="flex justify-between border-b-2 border-gray-900 pb-6 mb-8">
        <div>
          <h1 data-document className="text-3xl font-bold uppercase">ACME SOLUTIONS</h1>
          <p className="text-sm text-gray-600">contact@acmesolutions.com</p>
        </div>
        <div className="text-right">
          <h2 className="text-3xl font-bold text-gray-900 uppercase tracking-widest">{doc.type}</h2>
          <p className="font-semibold text-gray-700 mt-2">Ref: {doc.reference_number || doc.id.substring(0,8)}</p>
          <p className="text-gray-600">Date: {format(new Date(doc.created_at || generatedFallback), 'MMM dd, yyyy')}</p>
        </div>
      </div>
      
      <div className="mb-10">
        <h3 className="font-bold text-gray-800 mb-2">Billed To:</h3>
        <div className="p-4 bg-gray-50 border border-gray-200 rounded">
          <p className="font-bold text-lg">{td.clientName}</p>
        </div>
      </div>

      <table className="w-full text-left mb-8 border-collapse">
        <thead>
          <tr className="bg-gray-100 border-b-2 border-gray-800">
            <th className="py-3 px-4 font-bold text-gray-800">Description</th>
            <th className="py-3 px-4 font-bold text-gray-800 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {td.lineItems?.map((item: any, i: number) => (
            <tr key={i} className="border-b border-gray-200">
              <td className="py-4 px-4">{item.description}</td>
              <td className="py-4 px-4 text-right font-mono tabular-nums">{formatINR(item.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-end">
        <div className="w-64 space-y-3">
          <div className="flex justify-between">
            <span className="font-semibold text-gray-600">Subtotal:</span>
            <span className="font-mono tabular-nums">{formatINR(td.subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-semibold text-gray-600">Tax (18%):</span>
            <span className="font-mono tabular-nums">{formatINR(td.tax)}</span>
          </div>
          <div className="flex justify-between border-t-2 border-gray-800 pt-3 text-lg font-bold">
            <span>Total:</span>
            <span className="font-mono tabular-nums">{formatINR(td.total)}</span>
          </div>
        </div>
      </div>
      
      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          body * { visibility: hidden; }
          #print-doc-${doc.id}, #print-doc-${doc.id} * { visibility: visible; }
          #print-doc-${doc.id} { position: absolute; left: 0; top: 0; width: 100%; height: 100%; padding: 40px; margin: 0; box-sizing: border-box; }
        }
      `}} />
    </div>
  );
}
